import MiniIngredientTable from '@app/components/tables/ingredients-table/mini-ingredient-table';
import {
  FoodActivityRetrieveUpdate,
  FoodActivityRetrieveUpdateModelledFood,
  FoodOperationActivityDetail,
  FoodOperationActivityDetailModelledFood,
  IngredientDetail,
  IngredientDetailModelledFood,
} from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import { RepeatableFormRows } from '@shared/components/form/repeatable-form-rows';
import RecipeBaseFoodRow, {
  emptyBaseFoodRow,
} from '@shared/components/product-form/components/recipe-base-food-row';
import { useGetUnitUuid } from '@shared/components/product-form/hooks/use-get-unit-uuid';
import RecipeGenerator from '@shared/components/reggie/recipe-generator';
import { Button } from '@shared/components/ui/button';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

export interface ModelledFoodProps {
  item:
    | IngredientDetail
    | FoodActivityRetrieveUpdate
    | FoodOperationActivityDetail;
  isSubmitting: boolean;
  modelledFood:
    | IngredientDetailModelledFood
    | FoodOperationActivityDetailModelledFood
    | FoodActivityRetrieveUpdateModelledFood;
}

export default function ModelledFood({
  item,
  isSubmitting,
  modelledFood,
}: ModelledFoodProps) {
  const { setValue, resetField } = useFormContext();
  const defaultUnit = useGetUnitUuid('g');
  const [generatingRecipe, setGeneratingRecipe] = useState(false);

  const [isEditingRecipe, setIsEditingRecipe] = useState(
    Boolean(!modelledFood)
  );

  const hasModelledFood = Boolean(modelledFood);

  useEffect(() => {
    hasModelledFood && setIsEditingRecipe(false);
  }, [hasModelledFood, item]);

  return (
    <fieldset className="space-y-4" disabled={isSubmitting || generatingRecipe}>
      {!isEditingRecipe && (
        <div className="-mb-6 flex items-center justify-end">
          <Button
            variant="link"
            onClick={(e) => {
              e.preventDefault();
              setIsEditingRecipe(true);
            }}
          >
            Edit Recipe
          </Button>
        </div>
      )}
      {isEditingRecipe && (
        <>
          {!hasModelledFood && (
            <div className=" grid grid-cols-12 items-end gap-2 space-y-2">
              <InputFormField
                name="recipeDescription"
                label="Description"
                className="col-span-6"
              />
              <RecipeGenerator
                formName="recipeDescription"
                onLoadingChange={(loading) => setGeneratingRecipe(loading)}
                onGenerated={(generated) => {
                  setValue(
                    'ingredients',
                    generated.ingredients
                      .map((ingredient) => ({
                        entityUuid: ingredient.base_food_uuid,
                        quantity: ingredient.weight,
                        unitUuid: defaultUnit,
                        name: ingredient.base_food_name,
                        entityType: 'base_food',
                      }))
                      .sort((a, b) => b.quantity - a.quantity)
                  );
                }}
              />
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-end">
              {isEditingRecipe && hasModelledFood && (
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsEditingRecipe(false);
                    resetField('ingredients');
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
          <div className="mb-10">
            <RepeatableFormRows
              name="ingredients"
              component={RecipeBaseFoodRow}
              newRowObject={emptyBaseFoodRow}
            />
          </div>
        </>
      )}

      {!isEditingRecipe && hasModelledFood && (
        <MiniIngredientTable data={modelledFood!.ingredients} />
      )}
    </fieldset>
  );
}
